import { Component, effect, ElementRef, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { map } from 'rxjs';
import { TranslatePipe } from '../../i18n/translate.pipe';
import { AuthService } from '../../oauth/auth.service';
import { TenantService } from '../../tenants/tenant.service';

@Component({
  selector: 'cmi-page-header',
  imports: [RouterLink, TranslatePipe],
  templateUrl: './page-header.html'
})
export class PageHeader {
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _tenantService = inject(TenantService);
  private readonly _authService = inject(AuthService);

  protected readonly selectedTenant = this._tenantService.selectedTenant;
  protected readonly tenants = this._tenantService.tenants;
  protected readonly isDropdownOpen = signal(false);
  protected readonly dropdownContainer = viewChild<ElementRef<HTMLDivElement>>('dropdownContainer');

  protected readonly hideTenantBar = toSignal(
    this._router.events.pipe(
      map(() => (this._route.firstChild?.snapshot.data['hideTenantBar'] as boolean) ?? false)
    ),
    { initialValue: true }
  );

  public constructor() {
    effect((onCleanup) => {
      if (this.isDropdownOpen()) {
        const handler = (event: MouseEvent): void => {
          const container = this.dropdownContainer()?.nativeElement;
          if (container && !container.contains(event.target as Node)) {
            this.isDropdownOpen.set(false);
          }
        };

        // Use setTimeout so the click that opened the dropdown doesn't immediately close it
        const timeoutId = setTimeout(() => document.addEventListener('click', handler));
        onCleanup(() => {
          clearTimeout(timeoutId);
          document.removeEventListener('click', handler);
        });
      }
    });
  }

  protected toggleDropdown(): void {
    this.isDropdownOpen.update((open) => !open);
  }

  protected selectTenant(name: string): void {
    this._tenantService.selectTenant(name);
    this.isDropdownOpen.set(false);
    window.location.assign('/');
  }

  protected closeDropdown(): void {
    this.isDropdownOpen.set(false);
  }

  protected logout(): void {
    void this._authService.revokeTokens();
    void this._authService.clearAuth();
    window.location.assign('/');
  }
}
